import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment';
import { Star } from 'lucide-react';

import { getAttendanceReportSession } from '../../../../services/StudentDashboardService';
import '../../Trainer/TrainerDashboard.css';
import '../../common/lms-pages.css';
import './attendance-report.css';

const STATUS = {
  present: { label: 'Present', pill: 'pgm-pill--good', line: 'You were marked present for this class.' },
  absent: { label: 'Not present', pill: 'pgm-pill--bad', line: 'You were not marked present for this class.' },
  commonOff: { label: 'No class (common off)', pill: '', line: 'No class was held this day — it does not count towards your attendance.' },
};

const when = (value, format) => (value && moment(value).isValid() ? moment(value).format(format) : '—');
const dayName = (d) => d.charAt(0).toUpperCase() + d.slice(1, 3);

const Stars = ({ value, label }) => {
  const n = Number(value) || 0;
  return (
    <span className="arp-stars" role="img" aria-label={`${label}: ${n} out of 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={14} className={i <= n ? 'is-on' : ''} aria-hidden="true" />
      ))}
      <b>{n || '—'}</b>
    </span>
  );
};

/**
 * One class from My Attendance Report, in full: what was taught, the
 * batch, course, trainer and timings, whether the student was there, and
 * the student's own feedback on it.
 *
 * The house view popup (List Attendance's): pgm-head naming the class,
 * sections down the body, Close alone in the footer.
 *
 * Loaded by id from GET /studentdashboard/attendance-report/:id, which
 * answers for the caller's own classes only and sends nothing about any
 * other student -- no roster, no headcount, no one else's feedback. So
 * there is nothing here to hide: it was never sent.
 */
const ClassDetailPopup = ({ id, onClose }) => {
  const [row, setRow] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setRow(null);
    setFailed(false);
    getAttendanceReportSession(id)
      .then((r) => {
        if (!alive) return;
        if (r) setRow(r);
        else setFailed(true);
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [id]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const status = STATUS[row?.status] || STATUS.absent;
  const batch = row?.batch || null;
  const topics = Array.isArray(row?.topics) ? row.topics : [];
  const fb = row?.feedback || null;

  return createPortal(
    <div className="pg-modal" role="dialog" aria-modal="true" aria-labelledby="arp-detail-title" onClick={onClose}>
      <div className="pg-modal-panel pg-modal-panel--wide" onClick={(e) => e.stopPropagation()}>
        <header className="pgm-head">
          <div className="pgm-head-main">
            <span className="pg-modal-eyebrow">Class</span>
            <h2 className="pgm-batch" id="arp-detail-title">
              {row ? when(row.date, 'dddd, D MMMM YYYY') : 'Loading…'}
            </h2>
            {row && (
              <dl className="pgm-ident">
                <div className="pgm-ident-row">
                  <dt>Batch</dt>
                  <dd>{batch?.name || '—'}</dd>
                </div>
                <div className="pgm-ident-row">
                  <dt>Course</dt>
                  <dd>{row.courseName || '—'}</dd>
                </div>
                <div className="pgm-ident-row">
                  <dt>Trainer</dt>
                  <dd>{row.trainerName || '—'}</dd>
                </div>
              </dl>
            )}
          </div>
          <button type="button" className="pg-modal-close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </header>

        <div className="pg-modal-body">
          {!row && !failed ? (
            <div className="pg-state">
              <span className="pg-spinner" role="status" aria-hidden="true" />
              <p className="pg-state-text">Loading the class...</p>
            </div>
          ) : failed ? (
            <p className="pgm-empty">This class could not be loaded. Close this and try again.</p>
          ) : (
            <>
              {/* Whether they were there, first: it is why the row was opened. */}
              <section className={`arp-detail-status arp-detail-status--${row.status}`}>
                <span className={`pgm-pill ${status.pill}`}>{status.label}</span>
                <p>{status.line}</p>
              </section>

              <section className="pgm-section">
                <h3 className="pgm-section-title">
                  What was taught
                  <span className="pgm-section-count">{topics.length}</span>
                </h3>
                {topics.length === 0 ? (
                  <p className="pgm-empty">
                    {row.isCommonOff ? 'No class was held, so nothing was taught.' : 'No topics were recorded for this class.'}
                  </p>
                ) : (
                  <table className="pgm-topics-table">
                    <thead>
                      <tr>
                        <th className="pgm-num">#</th>
                        <th>Topic</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topics.map((t, i) => (
                        <tr key={`${t}-${i}`}>
                          <td className="pgm-num">{i + 1}</td>
                          <td>{t}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>

              <section className="pgm-section">
                <h3 className="pgm-section-title">About the class</h3>
                <dl className="arp-facts">
                  <div>
                    <dt>Date</dt>
                    <dd>{when(row.date, 'D MMM YYYY')}</dd>
                  </div>
                  <div>
                    <dt>Mode</dt>
                    <dd>{batch?.mode || '—'}</dd>
                  </div>
                  <div>
                    <dt>Classroom</dt>
                    <dd>{batch?.classroomName || '—'}</dd>
                  </div>
                  <div>
                    <dt>Batch timing</dt>
                    <dd>
                      {batch?.startTime ? `${when(batch.startTime, 'h:mm A')} – ${when(batch.endTime, 'h:mm A')}` : '—'}
                    </dd>
                  </div>
                  <div>
                    <dt>Batch days</dt>
                    <dd>{batch?.days?.length ? batch.days.map(dayName).join(', ') : '—'}</dd>
                  </div>
                  <div>
                    <dt>Batch runs</dt>
                    <dd>
                      {batch?.startTime ? `${when(batch.startTime, 'D MMM YYYY')} – ${when(batch.endTime, 'D MMM YYYY')}` : '—'}
                    </dd>
                  </div>
                  {row.totalLectures !== null && (
                    <div>
                      <dt>Lectures</dt>
                      <dd>{row.totalLectures}</dd>
                    </div>
                  )}
                  <div>
                    <dt>Marked by the trainer</dt>
                    <dd>
                      {when(row.markedAt, 'D MMM YYYY, h:mm A')}
                      {row.updatedAt && row.markedAt && moment(row.updatedAt).diff(moment(row.markedAt), 'minutes') > 1 && (
                        <small>Updated {when(row.updatedAt, 'D MMM YYYY, h:mm A')}</small>
                      )}
                    </dd>
                  </div>
                </dl>
              </section>

              {!row.isCommonOff && (
                <section className="pgm-section">
                  <h3 className="pgm-section-title">Your feedback</h3>
                  {!fb ? (
                    <p className="pgm-empty">
                      {row.status === 'present'
                        ? 'You have not left feedback on this class yet.'
                        : 'Feedback can only be left on classes you attended.'}
                    </p>
                  ) : (
                    <div className="arp-fb">
                      <div className="arp-fb-ratings">
                        <div>
                          <span className="arp-fb-label">Class</span>
                          <Stars value={fb.rating} label="Class" />
                        </div>
                        <div>
                          <span className="arp-fb-label">Trainer</span>
                          {fb.trainerRating ? <Stars value={fb.trainerRating} label="Trainer" /> : <span className="arp-muted">Not rated</span>}
                        </div>
                      </div>
                      {fb.topicRatings.length > 0 && (
                        <ul className="arp-fb-topics">
                          {fb.topicRatings.map((t, i) => (
                            <li key={`${t.topic}-${i}`}>
                              <span className="arp-fb-topic">{t.topic}</span>
                              <Stars value={t.rating} label={t.topic} />
                              {t.comment && <p className="arp-fb-note">{t.comment}</p>}
                            </li>
                          ))}
                        </ul>
                      )}
                      {fb.comment && <p className="arp-fb-comment">{fb.comment}</p>}
                      <p className="arp-muted">Submitted {when(fb.submittedAt, 'D MMM YYYY, h:mm A')} · {fb.status}</p>
                      {fb.reply && (
                        <div className="arp-fb-reply">
                          <span className="arp-fb-label">
                            Reply{fb.repliedAt ? ` · ${when(fb.repliedAt, 'D MMM YYYY')}` : ''}
                          </span>
                          <p>{fb.reply}</p>
                        </div>
                      )}
                    </div>
                  )}
                </section>
              )}
            </>
          )}
        </div>

        <footer className="pg-modal-foot">
          <button type="button" className="pg-btn pg-btn--main" onClick={onClose}>
            Close
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
};

export default ClassDetailPopup;
